export { default as AppImages, type AppImagesKeys } from "./App_images";

// Animation constants & variants
export * from "./animations";

/**
 * API endpoints
 * Example: AUTH: {
 *    LOGIN: "/login",
 *    ..///
 * }
 */
export const API_ENDPOINTS = {};

/**
 * Cookie names
 */
export const COOKIE_NAMES = {
  AUTH_TOKEN: "auth-token",
  REFRESH_TOKEN: "refresh-token",
  LOCALE: "NEXT_LOCALE",
  THEME: "site-theme",
  USER_DATA: "user-data",
};

/**
 * Routes
 * Example: HOME: "/"
 */
export const ROUTES = {
  HOME: "/",
  DASHBOARD: "/dashboard",
  FARMER_SETUP: "/farmer-setup",
  LOGIN: "/auth/login",
  SIGNUP: "/auth/signup",
  FORGOT_PASSWORD: "/auth/forgot-password",
};

/**
 * Application timing constants (in milliseconds)
 * NOTE: These are fallback values. Primary values should come from siteConfig (env)
 */
export const TIMINGS = {
  // Query/Cache timing
  QUERY_STALE_TIME: 5 * 60 * 1000, // 5 minutes
  QUERY_GC_TIME: 60 * 60 * 1000, // 1 hour
  PAGINATION_STALE_TIME: 60 * 1000, // 1 minute
  PAGINATION_GC_TIME: 5 * 60 * 1000, // 5 minutes

  // UI timing
  DEBOUNCE_SEARCH: 300, // 300 milliseconds
  NOTIFICATION_DISPLAY: 5000, // 5 seconds
  ANIMATION_FADE: 500, // 500 milliseconds
  MENU_TRANSITION: 300, // 300 milliseconds for menu animations
  NETWORK_STATUS_TRANSITION: 2000, // 2 seconds

  // Storage timing
  ONE_YEAR_SECONDS: 365 * 24 * 60 * 60, // 1 year in seconds
  COOKIE_EXPIRY_DAYS: 30, // 30 days for "remember me"
};

/**
 * HTTP Status codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  REQUEST_TIMEOUT: 408,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
};

/**
 * Regular expressions for validation
 */
export const REGEX = {
  EMAIL: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  PASSWORD:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  URL: /^(ftp|http|https):\/\/[^ "]+$/,
};

/**
 * Pagination defaults
 */
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PER_PAGE: 10,
  PER_PAGE_OPTIONS: [10, 25, 50, 100, 200, 500],
};

/**
 * Cookie settings
 */
export const COOKIE_SETTINGS = {
  LOCALE_MAX_AGE: 31536000, // 1 year in seconds
  PATH: "/",
  AUTH_MAX_AGE: 86400, // 1 day in seconds
  SECURE: true,
  HTTP_ONLY: false, // Changed to false so JavaScript can access the auth token
  SAME_SITE: "lax" as const,
};

/**
 * CSS class utilities
 */
export const CSS_CLASSES = {
  ACTIVE_BUTTON: "bg-primary text-primary-foreground",
  INACTIVE_BUTTON:
    "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  INACTIVE_BUTTON_PRIMARY_HOVER:
    "bg-secondary text-secondary-foreground hover:bg-primary/10",
};

export const QUERY_STATE_MANAGERS = {
  CALLBACK_URL: "callbackUrl",
  API_KEY_VALUE: "key",
};

/**
 * HTTP Headers
 */
export const HTTP_HEADERS = {
  API_KEY: "X-API-Key",
  CONTENT_TYPE: "Content-Type",
  AUTHORIZATION: "Authorization",
  ACCEPT: "Accept",
  ACCEPT_LANGUAGE: "Accept-Language",
};

/**
 * MIME Types
 */
export const MIME_TYPES = {
  JSON: "application/json",
  FORM_DATA: "multipart/form-data",
  URL_ENCODED: "application/x-www-form-urlencoded",
  TEXT: "text/plain",
  HTML: "text/html",
};

/**
 * Storage keys for localStorage/sessionStorage
 */
export const STORAGE_KEYS = {
  RECENT_SEARCHES: "recentSearches",
};

/**
 * Application limits
 */
export const LIMITS = {
  MAX_RECENT_SEARCHES: 10,
  MAX_QUERY_RETRIES: 2,
};

/**
 * Crypto constants
 */
export const CRYPTO = {
  ENCRYPTED_PREFIX: "encrypted:",
  PBKDF2_ITERATIONS: 100000,
};

/**
 * Animation values for Framer Motion
 */
export const ANIMATION_VALUES = {
  HOVER_SCALE: 1.05,
  TAP_SCALE: 0.95,
  SCALE_DOWN: 0.95,
  OFFSET_Y: 16,
  OFFSET_Y_NEGATIVE: -16,
  OFFSET_X: -20,
  OFFSET_X_POSITIVE: 20,
  CARD_OFFSET_Y: 20,
  CARD_OFFSET_Y_NEGATIVE: -20,
};

/**
 * Animation durations (in seconds) for Framer Motion
 */
export const ANIMATION_DURATIONS = {
  FADE: 0.35,
  QUICK: 0.3,
  FAST: 0.2,
  STAGGER: 0.08,
  STAGGER_LIST: 0.1,
};

/**
 * Middleware paths to skip
 */
export const MIDDLEWARE_PATHS = {
  NEXT: "/_next",
  API: "/api",
  FAVICON: "favicon.ico",
};

/**
 * Date strings
 */
export const DATE_STRINGS = {
  EPOCH: "Thu, 01 Jan 1970 00:00:00 GMT",
};
